import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaEdit, FaTrash, FaCalendarAlt, FaSearch, FaCheck, FaTimes, FaUserMd } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import BannerSlider from '../components/BannerSlider';
import { useApp, useAutoRefresh, useRolePath, triggerRefresh } from '../context/AppContext';
import { getAppointments, createAppointment, updateAppointment, deleteAppointment, getPatients, getDoctors, getConsultFee } from '../services/api';

const EMPTY_FORM = {
  patientName: '', patientId: '', date: '', time: '',
  type: 'Consultation', status: 'Scheduled', notes: '', doctorId: '',
  reassignedDoctorId: '',
};
const TYPES = ['Consultation', 'Follow-up', 'Emergency', 'Routine Check', 'Telemedicine'];
const STATUSES = ['Scheduled', 'Completed', 'Cancelled', 'Pending'];

const Appointments = () => {
  const { showToast, user } = useApp();
  const navigate = useNavigate();
  const rp       = useRolePath();
  const role = user?.role || 'Doctor';
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showTnC, setShowTnC] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [consultFee, setConsultFee] = useState(900);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [apptRes, patRes, docRes] = await Promise.allSettled([
        getAppointments(filterStatus ? { status: filterStatus } : {}),
        role !== 'Patient' ? getPatients() : Promise.resolve({ data: { data: [] } }),
        role !== 'Doctor'  ? getDoctors() : Promise.resolve({ data: { data: [] } }),
      ]);
      if (apptRes.status === 'fulfilled') setAppointments(apptRes.value.data?.data || []);
      if (patRes.status === 'fulfilled') setPatients(patRes.value.data?.data || []);
      if (docRes.status === 'fulfilled') setDoctors(docRes.value.data?.data || []);
    } catch { setAppointments([]); }
    setLoading(false);
  }, [filterStatus, role]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useAutoRefresh(fetchAll, 'appointments');

  useEffect(() => {
    if (role === 'Patient') {
      getConsultFee().then(r => setConsultFee(r.data?.fee || 900)).catch(() => {});
    }
  }, [role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.reassignedDoctorId) delete payload.reassignedDoctorId;

      if (role === 'Patient') {
        payload.patientName = user.name;
        payload.patientId = user.id;
        delete payload.status; // backend sets 'Pending' for patient
      }

      if (editId) {
        const res = await updateAppointment(editId, payload);
        setAppointments(prev => prev.map(a => a.id === editId ? (res.data?.data || a) : a));
        showToast('Appointment updated!', 'success');
      } else {
        const res = await createAppointment(payload);
        setAppointments(prev => [...prev, res.data?.data]);
        showToast('Appointment booked!', 'success');
        triggerRefresh('appointments');
      }
      setShowModal(false); setForm(EMPTY_FORM); setEditId(null);
    } catch (err) {
      showToast(err.message || 'Failed to save appointment', 'error');
    }
    setSaving(false);
  };

  const handleEdit = (appt) => {
    setForm({
      patientName: appt.patientName || '',
      patientId: appt.patientId || '',
      date: appt.date || '',
      time: appt.time || '',
      type: appt.type || 'Consultation',
      status: appt.status || 'Scheduled',
      notes: appt.notes || '',
      doctorId: appt.doctorId || '',
      reassignedDoctorId: appt.reassignedDoctorId || '',
    });
    setEditId(appt.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this appointment?')) return;
    try {
      await deleteAppointment(id);
      setAppointments(prev => prev.filter(a => a.id !== id));
      showToast('Appointment deleted', 'error');
    } catch (err) { showToast(err.message || 'Delete failed', 'error'); }
  };

  const handleStatusChange = async (appt, newStatus) => {
    try {
      // Only send the status field — do NOT send doctorId/patientId to avoid nulling them
      const res = await updateAppointment(appt.id, { status: newStatus });
      setAppointments(prev => prev.map(a => a.id === appt.id ? (res.data?.data || { ...a, status: newStatus }) : a));
      showToast(`Appointment ${newStatus.toLowerCase()}`, 'success');
      triggerRefresh('appointments');
    } catch (err) { showToast(err.message || 'Update failed', 'error'); }
  };

  const filtered = appointments.filter(a =>
    (a.patientName?.toLowerCase().includes(search.toLowerCase()) ||
     a.type?.toLowerCase().includes(search.toLowerCase()) ||
     a.status?.toLowerCase().includes(search.toLowerCase()) ||
     a.doctorName?.toLowerCase().includes(search.toLowerCase()))
  );

  const getDoctorDisplay = (appt) => {
    if (appt.reassignedDoctorName) return `${appt.doctorName || ''} → ${appt.reassignedDoctorName}`;
    return appt.doctorName || (appt.doctorId ? `#${appt.doctorId}` : '-');
  };

  return (
    <div className="page-wrapper">
      <Header title="Appointments" />
      <Sidebar />
      <main className="page-content">
        <BannerSlider />
        <div className="page-header">
          <div>
            <h1><FaCalendarAlt style={{ color: 'var(--primary)', marginRight: 8 }} />Appointments</h1>
            <p>{appointments.length} total</p>
          </div>
          <button className="btn btn-primary" onClick={() => {
            if (role === 'Patient') setShowTnC(true);
            else { setForm(EMPTY_FORM); setEditId(null); setShowModal(true); }
          }}>
            <FaPlus /> {role === 'Patient' ? 'Book' : 'Add'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div className="search-bar" style={{ flex: 1, marginBottom: 0 }}>
            <FaSearch /><input placeholder="Search appointments..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '10px 12px', border: '2px solid var(--border)', borderRadius: 10, fontSize: 13, background: 'white' }}>
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><FaCalendarAlt className="empty-state-icon" /><h3>No appointments found</h3><p>{role === 'Patient' ? 'Book your first appointment' : 'Add an appointment to get started'}</p></div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Patient</th><th>Doctor</th><th>Date</th><th>Time</th><th>Type</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(appt => (
                    <tr key={appt.id}>
                      <td><strong>{appt.patientName}</strong></td>
                      <td>
                        <span style={{ fontSize: 13 }}>
                          <FaUserMd style={{ color: 'var(--primary)', marginRight: 4 }} />
                          {getDoctorDisplay(appt)}
                        </span>
                      </td>
                      <td>{appt.date ? new Date(appt.date + 'T00:00:00').toLocaleDateString('en-IN') : '-'}</td>
                      <td>{appt.time || '-'}</td>
                      <td>{appt.type}</td>
                      <td><span className={`status-badge status-${appt.status?.toLowerCase()}`}>{appt.status}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {/* Doctor approve/reject Pending appointments */}
                          {role === 'Doctor' && appt.status === 'Pending' && (
                            <>
                              <button className="action-btn" style={{ background: '#e8f5e9', color: '#2e7d32' }} onClick={() => handleStatusChange(appt, 'Scheduled')} title="Approve"><FaCheck /></button>
                              <button className="action-btn" style={{ background: '#ffebee', color: '#c62828' }} onClick={() => handleStatusChange(appt, 'Cancelled')} title="Reject"><FaTimes /></button>
                            </>
                          )}
                          {/* Admin approve Pending appointments */}
                          {role === 'Admin' && appt.status === 'Pending' && (
                            <button className="action-btn" style={{ background: '#e8f5e9', color: '#2e7d32' }} onClick={() => handleStatusChange(appt, 'Scheduled')} title="Approve"><FaCheck /></button>
                          )}
                          {/* Edit/Delete — doctor and admin only */}
                          {(role === 'Doctor' || role === 'Admin') && (
                            <>
                              <button className="action-btn action-btn-edit" onClick={() => handleEdit(appt)} title="Edit"><FaEdit /></button>
                              <button className="action-btn action-btn-delete" onClick={() => handleDelete(appt.id)} title="Delete"><FaTrash /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
      <BottomNav />

      {showTnC && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowTnC(false)}>
          <div className="modal-box" style={{ maxWidth: 480, textAlign: 'center' }}>
            <div className="modal-header">
              <h2>Terms &amp; Conditions</h2>
              <button className="modal-close" onClick={() => setShowTnC(false)}>×</button>
            </div>
            <div style={{ padding: '24px 32px 32px' }}>
              <p style={{ fontSize: 14, color: '#6b7a8d', lineHeight: 1.8, marginBottom: 28 }}>
                This payment is valid for <strong>7 days</strong> (including the day of consultation) which includes
                consultation and one review of the investigations advised within that period.
              </p>
              <div style={{ marginBottom: 32 }}>
                <div style={{ fontSize: 13, color: '#6b7a8d', marginBottom: 6 }}>Fees</div>
                <div style={{ fontSize: 36, fontWeight: 800, color: '#2d3a4b' }}>INR {consultFee}.00</div>
              </div>
              <button
                onClick={() => { setShowTnC(false); navigate(rp('/new-appointment'), { state: { agreed: true } }); }}
                style={{
                  width: '100%', padding: '16px 0', borderRadius: 50, border: 'none',
                  background: 'linear-gradient(135deg,#E8500A,#FF6B2C)', color: 'white', fontSize: 16, fontWeight: 700,
                  cursor: 'pointer', letterSpacing: 0.5,
                }}
              >
                I Agree
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="modal-header">
              <h2>{editId ? 'Edit Appointment' : (role === 'Patient' ? 'Book Appointment' : 'New Appointment')}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {/* Patient dropdown — admin/doctor select patient; patient auto-assigned */}
              {role !== 'Patient' && (
                <div className="form-group">
                  <label>Patient *</label>
                  {patients.length > 0 ? (
                    <select required value={form.patientId} onChange={e => {
                      const pObj = patients.find(pt => String(pt.userId) === e.target.value || String(pt.id) === e.target.value);
                      // Use userId so the patient's own filter (patientId = req.user.id) matches
                      const pid = pObj?.userId ? String(pObj.userId) : e.target.value;
                      setForm(prev => ({ ...prev, patientId: pid, patientName: pObj ? pObj.name : '' }));
                    }}>
                      <option value="">Select patient...</option>
                      {patients.map(p => <option key={p.id} value={p.userId || p.id}>{p.name}</option>)}
                    </select>
                  ) : (
                    <input required value={form.patientName} onChange={e => setForm(p => ({ ...p, patientName: e.target.value }))} placeholder="Patient name" />
                  )}
                </div>
              )}

              {/* Doctor dropdown — patient selects required; admin shows current + can change; doctor sees self */}
              {role !== 'Doctor' && doctors.length > 0 && (
                <div className="form-group">
                  <label>Doctor {role === 'Patient' ? '*' : ''}</label>
                  <select
                    required={role === 'Patient'}
                    value={form.doctorId}
                    onChange={e => setForm(p => ({ ...p, doctorId: e.target.value }))}
                  >
                    <option value="">Select doctor...</option>
                    {doctors.map(d => (
                      <option key={d.id} value={d.id}>{d.name}{d.specialization ? ` — ${d.specialization}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Admin: forward/reassign to another doctor */}
              {role === 'Admin' && editId && doctors.length > 0 && (
                <div className="form-group">
                  <label>Forward Case to Doctor (optional)</label>
                  <select
                    value={form.reassignedDoctorId}
                    onChange={e => setForm(p => ({ ...p, reassignedDoctorId: e.target.value }))}
                  >
                    <option value="">None (keep original doctor)</option>
                    {doctors.map(d => (
                      <option key={d.id} value={d.id}>{d.name}{d.specialization ? ` — ${d.specialization}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" required value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Time *</label>
                  <input type="time" required value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Type</label>
                  <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                    {TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                {role !== 'Patient' && (
                  <div className="form-group">
                    <label>Status</label>
                    <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                      {STATUSES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes..." />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>
                  {saving ? 'Saving...' : editId ? 'Update' : (role === 'Patient' ? 'Book Appointment' : 'Add Appointment')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;
